import React, {
  ComponentProps,
  useState,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { Image as DesignSystemImage } from '@plerionhq/design-system';
import { requestImageThroughBackground } from '../../browser-extension/utils/image-utils';
import { isExtension } from '../../browser-extension/utils/extension-utils';
import { generatePublicImagePath } from './generatePublicImagePath';

import { useTranslation } from 'react-i18next';

// Module-level cache to prevent image re-fetching on component remounts
const imageCache = new Map<string, string>();

interface ExtensionImageProps extends ComponentProps<typeof DesignSystemImage> {
  /**
   * Whether to use the extension-safe image loading
   * If not specified, will auto-detect if running in extension context
   */
  useExtensionSafe?: boolean;
}

export const ExtensionImage = (props: ExtensionImageProps) => {
  const { t } = useTranslation('pleri/shared');
  const {
    useExtensionSafe,
    type,
    name,
    fileExtension,
    src,
    fallback,
    ...imageProps
  } = props;
  const fallbackSrc = fallback?.src;
  const fallbackType = fallback?.type;
  const fallbackName = fallback?.name;
  const fallbackFileExtension = fallback?.fileExtension;

  // Auto-detect if we should use extension-safe loading
  const shouldUseExtensionSafe = useExtensionSafe ?? isExtension;

  // Memoize image URLs (primary first, then fallback) to prevent unnecessary recalculations
  const imageUrls = useMemo(() => {
    if (!shouldUseExtensionSafe) {
      return [];
    }

    const baseUrl = process.env.API_URL || window?.location?.host;
    const resolvedBaseUrl = `https://${baseUrl}`;
    const urls: string[] = [];

    const primaryUrl = generatePublicImagePath({
      src,
      type,
      name,
      baseUrl: resolvedBaseUrl,
      fileExtension: fileExtension || 'png',
    });

    if (primaryUrl) {
      urls.push(primaryUrl);
    }

    if (fallbackSrc || fallbackType || fallbackName) {
      const fallbackUrl = generatePublicImagePath({
        src: fallbackSrc,
        type: fallbackType,
        name: fallbackName,
        baseUrl: resolvedBaseUrl,
        fileExtension: fallbackFileExtension || fileExtension || 'png',
      });

      if (fallbackUrl && !urls.includes(fallbackUrl)) {
        urls.push(fallbackUrl);
      }
    }

    return urls;
  }, [
    shouldUseExtensionSafe,
    src,
    type,
    name,
    fileExtension,
    fallbackSrc,
    fallbackType,
    fallbackName,
    fallbackFileExtension,
  ]);

  const cachedData =
    imageUrls.length > 0
      ? (imageUrls
          .map((url) => imageCache.get(url))
          .find((value): value is string => Boolean(value)) ?? null)
      : null;
  const [imageData, setImageData] = useState<string | null>(cachedData);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    if (!shouldUseExtensionSafe || imageUrls.length === 0) {
      return;
    }

    // Skip fetch if already cached
    if (cachedData) {
      setImageData(cachedData);
      setError(null);
      return;
    }

    let isCancelled = false;

    const loadImage = async () => {
      setIsLoading(true);
      setError(null);
      setImageData(null);

      try {
        for (const imageUrl of imageUrls) {
          const resolvedImageData = await requestImageThroughBackground(
            imageUrl,
            name,
          );

          if (resolvedImageData) {
            imageCache.set(imageUrl, resolvedImageData.dataUrl);

            // Only update state if component is still mounted and not cancelled
            if (isMountedRef.current && !isCancelled) {
              setImageData(resolvedImageData.dataUrl);
              setError(null);
            }

            return;
          }
        }

        if (isMountedRef.current && !isCancelled) {
          setError(t('failedToLoadImage'));
        }
      } catch (err) {
        console.error('Error loading image:', err);
        // Only update state if component is still mounted and not cancelled
        if (isMountedRef.current && !isCancelled) {
          setError(t('failedToLoadImage'));
        }
      } finally {
        // Only update state if component is still mounted and not cancelled
        if (isMountedRef.current && !isCancelled) {
          setIsLoading(false);
        }
      }
    };

    loadImage();

    // Cleanup function to cancel the request if component unmounts or dependencies change
    return () => {
      isCancelled = true;
    };
  }, [shouldUseExtensionSafe, name, imageUrls, cachedData, t]);

  // Cleanup function to prevent state updates on unmounted components
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // If not using extension-safe loading, fall back to the original component
  if (!shouldUseExtensionSafe) {
    return (
      <DesignSystemImage
        baseUrl={`https://${process.env.API_URL || window?.location?.host}`}
        {...imageProps}
        name={name}
        type={type}
        fileExtension={fileExtension}
        src={src}
        fallback={fallback}
      />
    );
  }

  // If no imageUrl can be generated (no name and no src), show a placeholder
  if (imageUrls.length === 0) {
    return (
      <div
        className={`flex items-center justify-center bg-gray-100 text-sm text-gray-500 dark:bg-gray-800 dark:text-gray-400 ${imageProps.className || ''}`}
        style={imageProps.style}
      >
        {t('noImageSpecified')}
      </div>
    );
  }

  // Show loading state
  if (isLoading) {
    return (
      <div
        className={`flex items-center justify-center bg-gray-100 dark:bg-gray-800 ${imageProps.className || ''}`}
        style={imageProps.style}
      >
        <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-gray-900"></div>
      </div>
    );
  }

  // Show error state
  if (error || !imageData) {
    return (
      <div
        className={`flex items-center justify-center bg-gray-100 text-sm text-gray-500 dark:bg-gray-800 dark:text-gray-400 ${imageProps.className || ''}`}
        style={imageProps.style}
      >
        {error || t('imageNotFound')}
      </div>
    );
  }

  // Render the image using the data URL
  return (
    <img
      src={imageData}
      alt={name || t('image')}
      className={imageProps.className}
      style={imageProps.style}
      {...(imageProps as Record<string, unknown>)}
    />
  );
};

export type { ExtensionImageProps };
